import { useCallback } from 'react'

import useFetch from 'src/hooks/useFetch'
import { COGNITO_OFF, CognitoConfig } from 'src/utils/cognito'
import { API } from 'src/utils/config'

const useAuthenticationService = () => {
  const [, executeLogout] = useFetch(
    { url: API.USER_LOGOUT, method: 'DELETE' },
    { manual: true },
    false,
  )

  const [, executeLogin] = useFetch(
    { url: API.USER_LOGIN, method: 'POST' },
    { manual: true },
    false,
  )

  const [, executeConnectAs] = useFetch<any>(
    { url: API.USER_CONNECT_AS, method: 'PUT' },
    { manual: true },
    false,
  )

  const [, executeCognitoConfig] = useFetch<CognitoConfig>(
    { url: API.COGNITO_CONFIG, method: 'GET' },
    { manual: true },
    false,
  )

  const [, executeCognitoToken] = useFetch(
    { url: API.COGNITO_TOKEN, method: 'POST' },
    { manual: true },
    false,
  )

  const [, executeKagemushaToken] = useFetch(
    { url: API.KAGEMUSHA_TOKEN, method: 'POST' },
    { manual: true },
    false,
  )

  // Whether adomik_api has the Cognito login on. Any failure means the classic
  // login: that is the rollback path, so it must never depend on this call.
  const handleGetCognitoConfig = useCallback(async (): Promise<CognitoConfig> => {
    try {
      const { data } = await executeCognitoConfig()
      return data ?? COGNITO_OFF
    } catch {
      return COGNITO_OFF
    }
  }, [executeCognitoConfig])

  // After the Cognito callback, adomik_api holds a session (cookie) for the
  // user: it hands back the same JWT as the password login, in the header.
  const handleExchangeCognitoSession = useCallback(async (): Promise<string | undefined> => {
    const { headers } = await executeCognitoToken()
    return headers['authorization']
  }, [executeCognitoToken])

  // The intranet's one-time impersonation link: adomik_api trades it for the
  // kagemusha's token, in the header, without touching any session.
  const handleExchangeKagemushaLink = useCallback(
    async (token: string): Promise<string | undefined> => {
      const { headers } = await executeKagemushaToken({ data: { token } })
      return headers['authorization']
    },
    [executeKagemushaToken],
  )

  const handleExecuteConnectAs = useCallback(
    async (sellerId: string) => {
      const { data } = await executeConnectAs({
        data: {
          user: {
            default_company_id: sellerId,
          },
        },
      })

      return data
    },
    [executeConnectAs],
  )

  const handleExecuteLogin = useCallback(
    async ({
      email,
      password,
      remember,
    }: {
      email: string
      password: string
      remember: boolean
    }) => {
      const { data, headers } = await executeLogin({
        data: { user: { email, password, remember } },
      })

      return { data, token: headers['authorization'] }
    },
    [executeLogin],
  )

  return {
    executeLogout,
    executeLogin: handleExecuteLogin,
    executeConnectAs: handleExecuteConnectAs,
    getCognitoConfig: handleGetCognitoConfig,
    exchangeCognitoSession: handleExchangeCognitoSession,
    exchangeKagemushaLink: handleExchangeKagemushaLink,
  }
}

export default useAuthenticationService
